import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { ValidatorForm } from "react-material-ui-form-validator";
import {
	FormActions,
	FormButton,
	FormSubmitButton,
	Dialog,
} from "frame/components";
import { useSelector } from "react-redux";
import { ajax } from "rxjs/ajax";
import { authHeaders, getOrganisationBaseUrl } from "frame";

import {
	strip,
	TitleSiteForm,
	PeopleForm,
	EditNotesForm,
	UploadFormCard,
	EmailRecipientsForm,
} from "features/forms";

import ReadOnlyForm from "./read-only-form";

const useStyles = makeStyles((theme) => ({
	root: {
		flex: 1,
		flexDirection: "row",
		padding: theme.spacing(3, 3, 10, 3),
	},
	actions: {
		paddingBottom: theme.spacing(2),
	},
	body: {
		// marginTop: theme.spacing(-3),
	},
}));

const hydrate = ({
	id,
	title,
	siteId,
	createdAt,
	people,
	otherPeople,
	operationalIssues,
	generalIssuesDiscussed,
	attachments,
	recipients,
	recipientsOther,
	emailLog,
	organisationId,
}) => ({
	id,
	title,
	siteId,
	createdAt,
	people,
	otherPeople,
	notes: {
		operationalIssues,
		generalIssuesDiscussed,
	},
	recipients,
	recipientsOther,
	emailLog: emailLog || [],
	attachments,
	currentOrganisation: organisationId,
});

const Form = ({ id, values, onClose, onChange, onReset, onSave, readOnly }) => {
	const classes = useStyles();
	const safetyMinutesList = useSelector(
		({ healthSafety }) => healthSafety.safetyMinutes?.results
	);
	const currentOrganisation = useSelector(
		({ organisations }) => organisations.currentOrganisation
	);

	const [loaded, setLoaded] = useState(false);
	const open = values && values.id;

	useEffect(() => {
		if (loaded) {
			return;
		}
		async function fetchData() {
			setLoaded(true);

			if (readOnly) {
				// Always fetch fresh from the API in view mode so the email audit log is up to date
				try {
					const response = await ajax({
						url: `${getOrganisationBaseUrl({ organisations: { currentOrganisation } })}/safety-minutes/${id}`,
						method: 'GET',
						headers: authHeaders(),
					}).toPromise();

					if (response && response.response) {
						onChange(hydrate(response.response));
					}
				} catch (error) {
					console.error("Error fetching safety minutes:", error);
				}
			} else {
				// In edit mode use the cached list entry for performance
				const safetyMinutesFromList = safetyMinutesList && safetyMinutesList.filter((s) => s.id === id)[0];

				if (safetyMinutesFromList) {
					onChange(hydrate(safetyMinutesFromList));
				} else {
					try {
						const response = await ajax({
							url: `${getOrganisationBaseUrl({ organisations: { currentOrganisation } })}/safety-minutes/${id}`,
							method: 'GET',
							headers: authHeaders(),
						}).toPromise();

						if (response && response.response) {
							onChange(hydrate(response.response));
						}
					} catch (error) {
						console.error("Error fetching safety minutes:", error);
					}
				}
			}
		}

		if (id) {
			fetchData();
		} // eslint-disable-next-line
	}, [id, loaded, onChange]);

	useEffect(() => {
		values.save.success && onClose();
	}, [values.save.success, onClose]);

	const onSubmit = (event) => {
		event.preventDefault();
		onSave(strip(values));
	};

	const onCancel = () => {
		onReset();
		onClose();
	};

	if (!open) {
		return null;
	}

	return (
		<Dialog
			open={open ? true : false}
			aria-labelledby="Safety Minutes"
			// breadcrumbs="Safety Minutes"
			title={readOnly ? "View Safety Minutes" : "Edit Safety Minutes"}
			onClose={onCancel}
		>
			<ValidatorForm autoComplete="off" noValidate onSubmit={onSubmit}>
				<div className={classes.body}>
					{readOnly ? (
						<ReadOnlyForm values={values} />
					) : (
						<>
							<TitleSiteForm
								disableDate
								values={values}
								onChange={onChange}
							/>
							<PeopleForm
								defaultStaffRole="Staff"
								values={values}
								onChange={onChange}
							/>
							<EditNotesForm
								values={values}
								onChange={onChange}
							/>
							<UploadFormCard
								title={"Edit Attachments"}
								values={values}
								onChange={onChange}
							/>
							<EmailRecipientsForm
								values={values}
								onChange={onChange}
							/>
						</>
					)}
				</div>
				<FormActions className={classes.actions}>
					{!readOnly && (
						<FormSubmitButton
							disabled={values.locked}
							loading={values.save.saving}
						>
							Save
						</FormSubmitButton>
					)}
					<FormButton
						disabled={values.save.saving}
						onClick={onCancel}
					>
						{readOnly ? "Close" : "Cancel"}
					</FormButton>
				</FormActions>
			</ValidatorForm>
		</Dialog>
	);
};

Form.propTypes = {
	id: PropTypes.string.isRequired,
	values: PropTypes.object.isRequired,
	onClose: PropTypes.func.isRequired,
	onChange: PropTypes.func.isRequired,
	onReset: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	readOnly: PropTypes.bool,
};

export default Form;
